/**
 * FootballPlayoffStandings — iter87.230. Round-by-round bracket view
 * for NFL Playoffs / CFB Playoffs. Sibling of MlbPlayoffStandings but
 * simpler: single-elimination straight games (no best-of-N series),
 * built from `tournament.nfl.rounds` / `tournament.cfb.rounds`, served
 * by `GET /nfl-po/tournaments/{id}/bracket` or `/cfb-po/.../bracket`.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const CONF_PILL = {
    AFC: "bg-heat/10 border border-heat/30 text-heat",
    NFC: "bg-volt/10 border border-volt/30 text-volt",
};

export default function FootballPlayoffStandings({ tournamentId, template, teams }) {
    const [bracket, setBracket] = useState(null);

    const teamFor = (id) => (teams || []).find((t) => t.id === id);
    const nameFor = (id) => teamFor(id)?.name || "TBD";
    const seedFor = (id) => teamFor(id)?.seed;
    const confFor = (id) => teamFor(id)?.conference;

    useEffect(() => {
        if (!tournamentId || !template) return;
        const fetcher = template === "nfl_playoffs_api" ? api.getNflPoBracket : api.getCfbPoBracket;
        fetcher(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [tournamentId, template]);

    if (!bracket || !bracket.rounds?.length) {
        return (
            <div className="brutal-card p-4 sm:p-5 text-center text-zinc-500 text-xs uppercase tracking-widest" data-testid="football-po-standings-empty">
                Playoff bracket not synced yet
            </div>
        );
    }

    const TeamRow = ({ id, isWinner, isLoser, score }) => (
        <div
            className={`flex items-center justify-between px-2 py-2 rounded-lg border transition-colors ${isWinner ? "bg-cyber/10 border-cyber/30" : "border-transparent"}`}
        >
            <div className="flex items-center min-w-0 gap-2">
                <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-white/[0.04] border border-white/[0.08] font-mono text-[9px] font-bold text-zinc-400 shrink-0">
                    {seedFor(id) ?? "\u2013"}
                </span>
                {confFor(id) && CONF_PILL[confFor(id)] && (
                    <span className={`inline-flex items-center rounded-full text-[8px] font-bold px-1.5 py-0.5 tracking-wider uppercase shrink-0 ${CONF_PILL[confFor(id)]}`}>
                        {confFor(id)}
                    </span>
                )}
                <span className={`text-sm font-semibold truncate flex-1 ${isWinner ? "text-cyber" : isLoser ? "text-zinc-500 line-through" : "text-zinc-200"}`}>
                    {nameFor(id)}
                </span>
            </div>
            <span className={`font-mono font-black text-sm tabular-nums ml-2 ${isWinner ? "text-white" : "text-zinc-500"}`}>{score ?? "\u2013"}</span>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="football-po-standings">
            {bracket.rounds.map((r) => {
                const games = r.games || [];
                return (
                    <div key={r.key} data-testid={`football-po-standings-round-${r.key}`}>
                        <h4 className="font-display font-extrabold uppercase text-base sm:text-lg tracking-tight mb-3 text-white border-b border-white/[0.08] pb-2 flex items-center justify-between">
                            <span>{r.label}</span>
                            <span className="text-[10px] font-mono text-zinc-500 normal-case tracking-normal">win = {r.points} pts</span>
                        </h4>
                        {games.length === 0 ? (
                            <p className="text-xs text-zinc-500 py-2" data-testid={`football-po-standings-round-${r.key}-empty`}>Matchups not set yet</p>
                        ) : (
                            <div className="space-y-3">
                                {games.map((g) => (
                                    <div
                                        key={g.vendor_match_id}
                                        data-testid={`football-po-standings-game-${g.vendor_match_id}`}
                                        className="brutal-card-light border border-white/[0.08] p-3 rounded-lg flex flex-col gap-1.5"
                                    >
                                        <TeamRow id={g.home_team_id} isWinner={g.finished && g.winner_id === g.home_team_id}
                                                 isLoser={g.finished && g.winner_id !== g.home_team_id} score={g.home_score} />
                                        <TeamRow id={g.away_team_id} isWinner={g.finished && g.winner_id === g.away_team_id}
                                                 isLoser={g.finished && g.winner_id !== g.away_team_id} score={g.away_score} />
                                        {g.finished && (
                                            <p data-testid={`football-po-standings-game-${g.vendor_match_id}-summary`} className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mt-1 pt-2 border-t border-white/[0.04]">
                                                {nameFor(g.winner_id)} wins
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
